import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import { STRINGS } from '@/strings/strings';
import type { PreviewEnvelope, PreviewTile } from '../../types';
import { Eyebrow } from '../Eyebrow';
import { Klappzeile } from '../Klappzeile';
import { HintBox } from './HintBox';
import { StageView } from './StageView';
import { TileRow } from './TileRow';

/**
 * Vorschau screen orchestrator — Block 5 · Part 2 (Sebastian ruling
 * 2026-09-09). The screen has a fixed layout order:
 *
 *   1-3  Eyebrow "EURE VORSCHAU" + heading "Eure Figuren und vier
 *        Seiten aus dem Buch" + intro paragraph
 *   4    Large image (StageView) — arrows ALWAYS visible + clickable
 *        even when the active tile is not yet rendered; a not-ready
 *        tile paints the same loading state in the big view that the
 *        tile shows below
 *   5    Tile row with captions BELOW each tile ("Eure Figuren" /
 *        "Seite N im Buch")
 *   6    White block "Du siehst das ganze Buch, bevor es gedruckt
 *        wird" + body (Kontrollversprechen)
 *   7    Order button (gold, → /book/:id/bestellung)
 *   8    Book title in bold (envelope.title, quoted)
 *   9    "Zum Nachlesen" heading
 *   10   Klappzeile "Die Geschichte"       — opens the summary
 *   11   Klappzeile "Alle Seiten im Text"  — opens ALL twenty scene
 *        texts together (one click, not per-scene expanders)
 *   12   Second order button — rendered ONLY when one of the two
 *        rows is open
 *
 * NO price on this screen. NO title-edit field (title is display-only
 * per Sebastian ruling 2026-09-05). NO retry CTA (failed tiles show
 * static placeholder + copy per 2026-08-12). NO "restseiten" line.
 *
 * "So sieht eine fertige Seite aus" example block RESTORED per
 * Sebastian ruling 2026-09-17 (superseding the Block 5 drop 2026-09-09).
 * Sits BELOW the primary order button and ABOVE the title / Zum
 * Nachlesen section (position 7.5 in the layout order below). Heading
 * + explainer + a real finished-page image (`/Eiffelturm.jpg`) so the
 * customer sees what a printed page carries. Strings reuse
 * `book.preview.exampleImageHeading` / `examplePanelExplainer` /
 * `exampleImageAlt` which survived the 2026-09-09 drop unused.
 *
 * ═══ 5.6 · ARROWS ALWAYS VISIBLE ═══════════════════════════════════
 *
 * Pre-Block-5 arrows disappeared when the customer clicked to a
 * not-yet-rendered tile — StageView's `canGoPrev`/`canGoNext` gates
 * hid them at boundaries, and the placeholder path rendered a
 * separate component with NO arrows. Sebastian's ruling: "arrows are
 * ALWAYS visible and ALWAYS clickable, in every state. Do not disable
 * the arrow — images do not finish in order, so a disabled arrow
 * would often be wrong. Never white, never a dead end."
 *
 * Implementation:
 *   - `canGoPrev` / `canGoNext` on StageView are passed `true`
 *     unconditionally (clamping at boundaries happens inside the
 *     click handlers, silently — no user-visible dead click).
 *   - The not-ready branch renders `BigViewWithArrows` (arrows + a
 *     centred loader/copy chip) instead of the pre-Block-5
 *     `BigViewPlaceholder` (no arrows).
 */

const QUIET_LINE_CLASS = 'mx-auto max-w-[520px] text-center text-xs text-dream-navy/70';
const BUTTON_GOLD =
  'h-12 w-full rounded-lg bg-dream-gold px-8 text-base font-bold text-dream-navy hover:bg-dream-gold/90';

export interface PreviewScreenProps {
  readonly envelope: PreviewEnvelope;
  /** Re-fetch the envelope after a mutation. */
  readonly onRefetch: () => void;
}

export function PreviewScreen({ envelope, onRefetch }: PreviewScreenProps) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [summaryOpen, setSummaryOpen] = useState(false);
  const [allPagesOpen, setAllPagesOpen] = useState(false);

  // Retained on the prop surface for callers that mutate via other
  // endpoints; the Block 5 rewrite makes no mutation on this screen.
  void onRefetch;

  const totalSlots = 1 + envelope.previewTiles.length;

  // Clamp at boundaries silently — arrows stay visible + clickable
  // per 5.6; a click at the boundary is a no-op.
  const goPrev = useCallback(() => setActiveIndex((i) => Math.max(0, i - 1)), []);
  const goNext = useCallback(
    () => setActiveIndex((i) => Math.min(totalSlots - 1, i + 1)),
    [totalSlots],
  );

  // `activeTile` is the currently-shown preview tile or null when the
  // anchor is active. `activeIndex` is bounded to 0..totalSlots-1 so
  // the array read at index-1 is safe when activeIndex >= 1.
  const activeTile: PreviewTile | null =
    activeIndex === 0 ? null : envelope.previewTiles[activeIndex - 1]!;

  // Caption below the big view. "Eure Figuren" for the anchor,
  // "Seite N im Buch" for pages. Same wording as the tile captions
  // (one label per thing).
  const positionText =
    activeIndex === 0
      ? STRINGS.book.preview.positionCover
      : STRINGS.book.preview.positionPage.replace('{pageNo}', activeTile!.pageNo.toString());

  // Sebastian ruling 2026-09-24 — on-image counter pill ("1 von 5")
  // in the top-right corner of the big view. Real slot count
  // (anchor + previewTiles.length). Passed into StageView and the
  // not-ready BigViewWithArrows so the counter is present in every
  // big-view state.
  const positionCounter = STRINGS.book.preview.positionCounter
    .replace('{current}', String(activeIndex + 1))
    .replace('{total}', String(totalSlots));

  const tiles = [
    { kind: 'cover' as const, imageUrl: envelope.cover.imageUrl },
    ...envelope.previewTiles.map((tile) => ({
      kind: 'page' as const,
      pageNo: tile.pageNo,
      szeneLetter: tile.szeneLetter,
      state: tile.state,
      composedImageUrl: tile.composedImageUrl,
      rawImageUrl: tile.rawImageUrl,
    })),
  ];

  return (
    <div className="space-y-6 py-4">
      {/* 1-3 · Eyebrow + heading + intro */}
      <div className="flex flex-col gap-3">
        <Eyebrow text={STRINGS.book.preview.eyebrow} />
        <h1 className="text-2xl font-semibold text-dream-navy">{STRINGS.book.preview.heading}</h1>
        <p className="text-base leading-relaxed text-dream-navy/80">
          {STRINGS.book.preview.headingSub}
        </p>
      </div>

      {/* 4 · Large image with always-visible arrows */}
      {activeIndex === 0 ? (
        <StageView
          slot={{
            kind: 'cover',
            imageUrl: envelope.cover.imageUrl,
            imageAlt: STRINGS.book.preview.thumbCoverLabel,
          }}
          canGoPrev
          canGoNext
          onPrev={goPrev}
          onNext={goNext}
          counter={positionCounter}
        />
      ) : activeTile!.state === 'ready' &&
        (activeTile!.rawImageUrl || activeTile!.composedImageUrl) ? (
        <StageView
          slot={{
            kind: 'page',
            rawImageUrl: activeTile!.rawImageUrl,
            composedImageUrl: activeTile!.composedImageUrl,
            imageAlt: STRINGS.book.preview.thumbPageLabel.replace(
              '{pageNo}',
              activeTile!.pageNo.toString(),
            ),
          }}
          canGoPrev
          canGoNext
          onPrev={goPrev}
          onNext={goNext}
          counter={positionCounter}
        />
      ) : (
        <BigViewWithArrows
          state={activeTile!.state === 'ready' ? 'blocked' : activeTile!.state}
          onPrev={goPrev}
          onNext={goNext}
          counter={positionCounter}
        />
      )}

      <p className={QUIET_LINE_CLASS}>{positionText}</p>

      {/* 5 · Tile row (captions below each tile) */}
      <TileRow tiles={tiles} activeIndex={activeIndex} onSelect={setActiveIndex} />

      {/* 6 · White block from 5.7 */}
      <HintBox>
        <p className="text-base font-semibold text-dream-navy leading-relaxed">
          {STRINGS.book.preview.kontrollversprechenHeading}
        </p>
        <p className="mt-2 text-sm text-dream-navy/70 leading-relaxed">
          {STRINGS.book.preview.kontrollversprechenBody}
        </p>
      </HintBox>

      {/* 7 · Order button */}
      <PreviewOrderCta bookOrderId={envelope.bookOrderId} />

      {/* 7.5 · "Beispiel aus einem anderen Buch: So sieht eine
             fertige Seite aus" example block (Sebastian ruling
             2026-09-17, heading updated 2026-09-24). Sits below the
             order button and above the title / Zum Nachlesen section.
             Full width of the parent container so it aligns with the
             HintBox above; the image is capped at max-w-[400px] and
             centred so it does not dominate the mobile viewport.
             Sebastian ruling 2026-09-24 — the image carries a
             diagonal "Beispiel" ribbon across the top-left corner
             so a scanning customer cannot mistake it for a page of
             their own book (the heading + ribbon are independent
             affordances). */}
      <section className="space-y-3" aria-labelledby="preview-example-heading">
        <h2 id="preview-example-heading" className="text-lg font-semibold text-dream-navy">
          {STRINGS.book.preview.exampleImageHeading}
        </h2>
        <p className="text-base leading-relaxed text-dream-navy/80">
          {STRINGS.book.preview.examplePanelExplainer}
        </p>
        <div className="relative mx-auto w-full max-w-[400px] overflow-hidden rounded-lg border border-dream-navy/15">
          <img
            src="/Eiffelturm.jpg"
            alt={STRINGS.book.preview.exampleImageAlt}
            className="block aspect-square w-full object-cover"
            loading="lazy"
            decoding="async"
          />
          {/* Diagonal ribbon in the top-left corner. Uses gold on
              navy for the same contrast as the CTAs on the screen.
              `aria-hidden` because screen readers get the same
              framing from the heading + alt text (verbatim above);
              the ribbon is a visual reinforcement, not a separate
              piece of information. */}
          <span
            aria-hidden
            className="pointer-events-none absolute -left-10 top-6 w-40 rotate-[-45deg] bg-dream-gold py-1 text-center text-xs font-bold uppercase tracking-wider text-dream-navy shadow-sm"
          >
            {STRINGS.book.preview.exampleRibbon}
          </span>
        </div>
      </section>

      {/* 8 · Book title in bold (only when non-empty) */}
      {envelope.title.length > 0 && (
        <p className="text-lg font-semibold text-dream-navy">„{envelope.title}"</p>
      )}

      {/* 9 · "Zum Nachlesen" heading */}
      <h2 className="text-lg font-semibold text-dream-navy">
        {STRINGS.book.preview.zumNachlesenHeading}
      </h2>

      {/* 10 · Die Geschichte — opens summary */}
      <div>
        <Klappzeile
          state={summaryOpen ? 'open' : 'closed'}
          label={STRINGS.book.preview.klappGeschichteLabel}
          onToggle={() => setSummaryOpen((v) => !v)}
          ariaControls="preview-summary-panel"
        />
        {summaryOpen && (
          <div id="preview-summary-panel" className="pt-3">
            <p className="text-base leading-relaxed text-dream-navy whitespace-pre-wrap">
              {envelope.zusammenfassung}
            </p>
          </div>
        )}
      </div>

      {/* 11 · Alle Seiten im Text — opens all 20 scenes at once */}
      <div>
        <Klappzeile
          state={allPagesOpen ? 'open' : 'closed'}
          label={STRINGS.book.preview.klappAllePagesLabel}
          onToggle={() => setAllPagesOpen((v) => !v)}
          ariaControls="preview-all-pages-panel"
        />
        {allPagesOpen && (
          <ol id="preview-all-pages-panel" className="pt-3 space-y-6">
            {envelope.pages.map((page) => (
              <li key={page.sceneId} className="space-y-2">
                <h3 className="text-base font-semibold text-dream-navy">
                  {STRINGS.book.geschichte.pageRowHeading.replace('{n}', page.pageNo.toString())}
                </h3>
                <p className="text-base leading-relaxed text-dream-navy whitespace-pre-wrap">
                  {page.text}
                </p>
              </li>
            ))}
          </ol>
        )}
      </div>

      {/* 12 · Second order button — only when one of the two rows
             above is open. Same button as position 7. */}
      {(summaryOpen || allPagesOpen) && <PreviewOrderCta bookOrderId={envelope.bookOrderId} />}
    </div>
  );
}

/**
 * Block 5 · 5.6 (Sebastian ruling 2026-09-09) — the big-view state for
 * a tile that is not yet rendered / failed / blocked. Same shape as
 * StageView (aspect-square, rounded-xl, shadow) so the layout does
 * not jump when the tile flips to ready. Arrows are always rendered
 * (never disabled) so the customer can navigate away from a
 * still-drawing tile — images don't finish in order, and dead-end is
 * the exact defect this replaces.
 */
interface BigViewWithArrowsProps {
  readonly state: 'rendering' | 'failed' | 'blocked';
  readonly onPrev: () => void;
  readonly onNext: () => void;
  /** Sebastian ruling 2026-09-24 — same counter pill StageView carries. */
  readonly counter?: string;
}

function BigViewWithArrows({ state, onPrev, onNext, counter }: BigViewWithArrowsProps) {
  const copy =
    state === 'rendering'
      ? STRINGS.book.preview.tileRendering
      : state === 'failed'
        ? STRINGS.book.preview.tileFailed
        : STRINGS.book.preview.tileBlocked;
  return (
    <div
      className="@container aspect-square w-full rounded-xl shadow-xl overflow-hidden relative bg-dream-white"
      aria-label={STRINGS.book.preview.bigViewAriaLabel}
      role="group"
    >
      <div className="flex h-full w-full flex-col items-center justify-center gap-3 px-6 text-center">
        {state === 'rendering' && (
          <Loader2 aria-hidden className="h-10 w-10 animate-spin text-dream-navy/50" />
        )}
        <p
          className={`text-base leading-relaxed ${
            state === 'rendering' ? 'text-dream-navy/70' : 'text-error'
          }`}
        >
          {copy}
        </p>
      </div>
      {counter !== undefined && counter.length > 0 && (
        <span
          aria-hidden
          className="absolute right-3 top-3 rounded-full bg-dream-navy/70 px-2.5 py-1 text-xs font-semibold text-dream-white shadow-sm"
        >
          {counter}
        </span>
      )}
      {/* Arrows — always visible, always clickable. Clamp happens in
          the click handler upstream. */}
      <button
        type="button"
        onClick={onPrev}
        aria-label={STRINGS.book.preview.navPrev}
        className="absolute left-3 top-1/2 -translate-y-1/2 h-11 w-11 rounded-full bg-dream-white/90 flex items-center justify-center text-dream-navy shadow-sm transition-colors hover:bg-dream-white focus-visible:outline-2 focus-visible:outline-dream-navy/50"
      >
        <ChevronLeft aria-hidden className="h-5 w-5" />
      </button>
      <button
        type="button"
        onClick={onNext}
        aria-label={STRINGS.book.preview.navNext}
        className="absolute right-3 top-1/2 -translate-y-1/2 h-11 w-11 rounded-full bg-dream-white/90 flex items-center justify-center text-dream-navy shadow-sm transition-colors hover:bg-dream-white focus-visible:outline-2 focus-visible:outline-dream-navy/50"
      >
        <ChevronRight aria-hidden className="h-5 w-5" />
      </button>
    </div>
  );
}

/**
 * Order button — gold, full-width, navigates to /book/:orderId/bestellung.
 * Rendered TWICE per Block 5 · 5.8: once at position 7 (below the white
 * block) and again at position 12 (below the two Klappzeile rows,
 * only when one of them is open).
 */
function PreviewOrderCta({ bookOrderId }: { bookOrderId: string }) {
  const navigate = useNavigate();
  return (
    <div className="pt-2">
      <button
        type="button"
        onClick={() => navigate(`/book/${bookOrderId}/bestellung`)}
        className={BUTTON_GOLD}
      >
        {STRINGS.book.preview.previewOrderCta}
      </button>
    </div>
  );
}
